import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion, useAnimation, useTransform } from 'framer-motion';
import useWindowSize from 'hooks/useWindowSize';
import { loadImage } from 'helpers';
import { SCREEN_SIZES } from 'style';

import Bg from 'products/global/assets/img/home/split-bg.png';
import BgMobile from 'products/global/assets/img/home/bg-mobile.png';
import Road from 'products/global/assets/img/home/road.png';
import Ship from 'products/global/assets/img/home/split-ship.png';
import Bird from 'products/global/assets/img/home/bird.png';
import People from 'products/global/assets/img/home/split-people.png';

export const StyledIllustration = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${Bg});
  background-size: cover;
  overflow: hidden;
`;

export const MobileBg = styled(motion.img).attrs({ src: BgMobile })`
  position: absolute;
  bottom: 18px;
  left: 0;
  width: 100%;
`;

export const RoadImg = styled(motion.img)`
  position: absolute;
  bottom: 0;
  height: auto;
  right: 21%;
  width: 30.2%;
`;

export const ShipImg = styled(motion.img)`
  position: absolute;
  bottom: 20%;
  right: 37%;
  height: auto;
  width: 41%;
`;

export const PeopleImg = styled(motion.img)`
  position: absolute;
  bottom: 0;
  left: 20.5%;
  width: 40%;
  height: auto;
`;

const images = [Road, Ship, Bird, People];
const mobileImages = [Bird, BgMobile];

const Illustration = ({
  allAnimationsFinished,
  onAnimationFinished,
  birdControls,
  ourStoryControls,
  roadX,
  roadY
}) => {
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < SCREEN_SIZES.tabletLandscape;
  const controls = useAnimation();

  const shipX = useTransform(roadX, (val) => val * 0.5);
  const shipY = useTransform(roadY, (val) => val * 0.5);

  const peopleX = useTransform(roadX, (val) => val * -0.15);
  const peopleY = useTransform(roadY, (val) => val * -0.7);

  const onImagesLoaded = useCallback(async () => {
    birdControls.start('finish');
    await Promise.all([controls.start('finish'), ourStoryControls.start('visible')]);
    onAnimationFinished();
  }, [controls, onAnimationFinished]);

  useEffect(() => {
    if (isMobile) {
      Promise.all(mobileImages.map(loadImage)).then(() => onImagesLoaded());
    } else {
      Promise.all(images.map(loadImage)).then(() => onImagesLoaded());
    }
  }, [onImagesLoaded]);

  useEffect(() => {
    if (!isMobile) {
      const handleMouseMove = (e) => {
        roadX.set(-0.03 * e.pageX);
        roadY.set(0.008 * e.pageY);
      };

      if (allAnimationsFinished) {
        window.addEventListener('mousemove', handleMouseMove);
      }
      return () => {
        if (allAnimationsFinished) {
          window.removeEventListener('mousemove', handleMouseMove);
        }
      };
    }
  }, [allAnimationsFinished]); // eslint-disable-line

  const parentVariants = {
    finish: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.4 }
    },
    start: {
      opacity: 0
    }
  };

  const childVariants = {
    finish: {
      y: 0,
      opacity: 1,
      transition: {
        y: {
          type: 'spring',
          damping: 300,
          stiffness: 200,
          restSpeed: 100,
          restDelta: 100
        }
      }
    },
    start: {
      y: window.innerHeight,
      opacity: 0
    }
  };

  return (
    <StyledIllustration variants={parentVariants} animate={controls} initial="start">
      {!isMobile && (
        <>
          <RoadImg
            src={Road}
            alt="road curving to the sky"
            variants={childVariants}
            style={allAnimationsFinished ? { x: roadX, y: roadY } : null}
          />
          <ShipImg
            src={Ship}
            alt="spaceship"
            variants={childVariants}
            style={allAnimationsFinished ? { x: shipX, y: shipY } : null}
          />
          <PeopleImg
            src={People}
            alt="bird waving"
            variants={childVariants}
            style={allAnimationsFinished ? { x: peopleX, y: peopleY } : null}
            transition={{ type: 'spring' }}
          />
        </>
      )}

      {isMobile && <MobileBg variants={childVariants} />}
    </StyledIllustration>
  );
};

Illustration.propTypes = {
  allAnimationsFinished: PropTypes.bool,
  onAnimationFinished: PropTypes.func,
  birdControls: PropTypes.object,
  ourStoryControls: PropTypes.object,
  roadX: PropTypes.object,
  roadY: PropTypes.object
};

export default Illustration;
